import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, RefreshCw, Sparkles, Loader2, Copy, Check, Trash2, Search, Plus, Hand, TrendingUp, Eye, MousePointerClick, UserPlus, ShoppingBag } from "lucide-react";
import { resolveImageUrl } from "../../components/ImageUpload";
import CopyLinkButton from "../../components/CopyLinkButton";
import HelpVideoButton from "../../components/HelpVideoButton";
import { bundleShareUrl } from "../../lib/shareLink";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Modal for building a 5-product WhatsApp share bundle.
 *
 * Flow:
 *   1. Manager picks category + brand + price range (all optional but at
 *      least one recommended so the suggestion is meaningful).
 *   2. Click "Подобрать 5 моделей" → hits `POST /admin/shares/suggest`,
 *      returns up to 5 candidates ordered by recency.
 *   3. Manager can swap any candidate: local `Заменить` button calls
 *      `POST /admin/shares/swap` with `exclude_ids` = currently-shown ids.
 *   4. Click "Создать подборку" → `POST /admin/shares` returns a token +
 *      the manager immediately gets the short URL to copy.
 *
 * Existing bundles from this manager appear at the bottom with their view/
 * signup counters and expiry — you can delete or re-copy the link.
 */
export default function ShareBuilder({ categories, brands, onClose, onInsert }) {
  const [filters, setFilters] = useState({
    category_slug: "", brand: "", price_min: "", price_max: "",
    query: "", desired_count: 5,
  });
  const [items, setItems] = useState([]);        // suggested/current bundle
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);  // { token, expires_at }
  const [myShares, setMyShares] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => { loadMyShares(); loadStats(); }, []);

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/shares/stats?period_days=7`,
                                  { withCredentials: true });
      setStats(r.data);
    } catch { /* noop */ }
  };

  const loadMyShares = async () => {
    try {
      const r = await axios.get(`${API}/admin/shares`, { withCredentials: true });
      setMyShares(r.data.items || []);
    } catch { /* noop */ }
  };

  const filterBody = () => ({
    category_slug: filters.category_slug || null,
    brand: filters.brand || null,
    price_min: filters.price_min ? Number(filters.price_min) : null,
    price_max: filters.price_max ? Number(filters.price_max) : null,
    query: (filters.query || "").trim() || null,
    desired_count: Number(filters.desired_count) || 5,
  });

  const suggest = async () => {
    setBusy(true); setError(""); setCreated(null);
    try {
      const r = await axios.post(`${API}/admin/shares/suggest`, filterBody(),
                                  { withCredentials: true });
      setItems(r.data.items || []);
      if ((r.data.items || []).length === 0) {
        setError("Нет товаров по этим фильтрам — расширьте диапазон цены или снимите категорию.");
      }
    } catch (e) {
      setError(humanize(e, "Не удалось получить подборку"));
    } finally { setBusy(false); }
  };

  const swap = async (idx) => {
    setBusy(true); setError("");
    try {
      const exclude = items.map(i => i.id);
      const r = await axios.post(`${API}/admin/shares/swap`,
                                  { ...filterBody(), exclude_ids: exclude },
                                  { withCredentials: true });
      const replacement = r.data.item;
      setItems(prev => prev.map((it, i) => i === idx ? replacement : it));
    } catch (e) {
      setError(humanize(e, "Нет других товаров для замены"));
    } finally { setBusy(false); }
  };

  const removeAt = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Manual pick — add a single product to the bundle from the search
  // panel. Silently ignores duplicates and caps at the manager's
  // chosen desired_count (default 5, max 15).
  const addProduct = (product) => {
    const cap = Math.max(3, Math.min(15, Number(filters.desired_count) || 5));
    setItems(prev => {
      if (prev.some(p => p.id === product.id)) return prev;
      if (prev.length >= cap) return prev;
      return [...prev, product];
    });
  };

  const createBundle = async () => {
    if (!items.length) return;
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/shares`, {
        product_ids: items.map(i => i.id),
        ...filterBody(),
      }, { withCredentials: true });
      setCreated({ token: r.data.token, expires_at: r.data.expires_at });
      await loadMyShares();
    } catch (e) {
      setError(humanize(e, "Не удалось создать подборку"));
    } finally { setBusy(false); }
  };

  const deleteBundle = async (id) => {
    if (!window.confirm("Удалить эту подборку? Ссылка перестанет работать.")) return;
    try {
      await axios.delete(`${API}/admin/shares/${id}`, { withCredentials: true });
      await loadMyShares();
    } catch (e) { alert(humanize(e, "Не удалось удалить")); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()} data-testid="share-builder">
        <div style={header}>
          <div>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#0A0A0A" }}>
              Создать WhatsApp-подборку
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89", marginTop: 4 }}>
              До 15 товаров · короткая ссылка на 7 дней · авто-подбор ИИ или ручной выбор
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <HelpVideoButton slug="video-share-builder" label="Как отправить подборку"
              testId="help-video-share-builder" />
            <button data-testid="share-builder-close" onClick={onClose} style={xBtn}><X size={18} /></button>
          </div>
        </div>

        <div style={{ padding: "0 24px 12px", display: "flex",
                      flexDirection: "column", gap: 8 }}>
          <input data-testid="share-query" type="text"
            placeholder="Опишите что нужно клиенту: «светлые полупальто без меха», «двубортное шерсть до 500к»…"
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            style={{ ...inp, fontSize: 12.5 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                            letterSpacing: 1, textTransform: "uppercase" }}>
              Моделей в подборке:
            </span>
            <input data-testid="share-desired-count" type="range"
              min={3} max={15} step={1}
              value={filters.desired_count}
              onChange={(e) => setFilters({ ...filters, desired_count: Number(e.target.value) })}
              style={{ flex: 1, accentColor: "#C5A059" }} />
            <span style={{ fontFamily: "Cormorant Garamond", fontSize: 22,
                            color: "#0A0A0A", fontWeight: 700, minWidth: 24,
                            textAlign: "center" }}>
              {filters.desired_count}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: "0 24px 12px", display: "grid",
                      gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <select data-testid="share-cat" value={filters.category_slug}
            onChange={(e) => setFilters({ ...filters, category_slug: e.target.value })} style={inp}>
            <option value="">Все категории</option>
            {(categories || []).map(c => (
              <option key={c.slug || c.id} value={c.slug}>{c.name || c.slug}</option>
            ))}
          </select>
          <select data-testid="share-brand" value={filters.brand}
            onChange={(e) => setFilters({ ...filters, brand: e.target.value })} style={inp}>
            <option value="">Все бренды</option>
            {(brands || []).map(b => (
              <option key={b.id || b.name} value={b.name || b.slug}>{b.name}</option>
            ))}
          </select>
          <input data-testid="share-price-min" type="number" placeholder="от ₸"
            value={filters.price_min}
            onChange={(e) => setFilters({ ...filters, price_min: e.target.value })} style={inp} />
          <input data-testid="share-price-max" type="number" placeholder="до ₸"
            value={filters.price_max}
            onChange={(e) => setFilters({ ...filters, price_max: e.target.value })} style={inp} />
        </div>
        <div style={{ padding: "0 24px 12px", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button data-testid="share-suggest-btn" onClick={suggest} disabled={busy}
            style={{ ...btnPrimary, flex: "1 1 200px", justifyContent: "center" }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Подобрать {filters.desired_count} {filters.query ? "по описанию (ИИ)" : "автоматически"}
          </button>
          <button data-testid="share-manual-btn" onClick={() => setShowManual(v => !v)}
            style={{ ...btnSecondary, flex: "1 1 200px", justifyContent: "center" }}>
            <Hand size={13} />
            {showManual ? "Скрыть ручной подбор" : "Выбрать вручную"}
          </button>
        </div>

        {error && (
          <div style={{ margin: "0 24px 12px", padding: "8px 12px",
                        background: "#fef2f2", border: "1px solid #fecaca",
                        borderRadius: 6, fontFamily: "Manrope", fontSize: 12, color: "#7f1d1d" }}>
            {error}
          </div>
        )}

        {showManual && (
          <ManualPicker filters={filters} onAdd={addProduct}
            selectedIds={items.map(i => i.id)}
            atMax={items.length >= (Number(filters.desired_count) || 5)} />
        )}

        {/* Suggested products */}
        {items.length > 0 && (
          <div style={{ padding: "8px 24px 4px", maxHeight: 340, overflowY: "auto" }}>
            <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                          letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
              Выбранные модели ({items.length} из {filters.desired_count})
            </div>
            {items.map((it, idx) => (
              <div key={it.id + idx} data-testid={`share-item-${idx}`}
                style={{ display: "flex", gap: 12, padding: "8px 0",
                         borderBottom: "1px solid #f1f1ee" }}>
                <div style={{ width: 54, height: 68, background: "#f5f5f4",
                              borderRadius: 4, overflow: "hidden", flexShrink: 0 }}>
                  {it.image && <img src={resolveImageUrl(it.image)} alt={it.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#0A0A0A",
                                fontWeight: 600, lineHeight: 1.3 }}>
                    {it.name}
                  </div>
                  <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89", marginTop: 2 }}>
                    {it.brand} · {Number(it.price).toLocaleString("ru-RU")} ₸
                  </div>
                </div>
                <button onClick={() => swap(idx)} disabled={busy}
                  data-testid={`share-swap-${idx}`}
                  title="Заменить" style={iconBtn}>
                  <RefreshCw size={13} />
                </button>
                <button onClick={() => removeAt(idx)} data-testid={`share-remove-${idx}`}
                  title="Убрать" style={iconBtn}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create button + resulting link */}
        {items.length > 0 && (
          <div style={{ padding: "12px 24px 16px", borderTop: "1px solid #f1f1ee" }}>
            {!created ? (
              <button data-testid="share-create-btn" onClick={createBundle} disabled={busy || !items.length}
                style={{ ...btnPrimary, width: "100%", justifyContent: "center", padding: "10px 16px" }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Создать подборку и получить ссылку
              </button>
            ) : (
              <div data-testid="share-created" style={{ background: "#f0fdf4",
                    border: "1px solid #bbf7d0", borderRadius: 8, padding: 12 }}>
                <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#14532d",
                              fontWeight: 600, marginBottom: 6 }}>
                  ✓ Подборка создана. Отправьте ссылку в WhatsApp:
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 12, color: "#0A0A0A",
                              background: "white", border: "1px solid #d4d4d4",
                              padding: "8px 10px", borderRadius: 6, wordBreak: "break-all",
                              marginBottom: 8 }}>
                  {bundleShareUrl(created.token)}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <CopyLinkButton url={bundleShareUrl(created.token)} testid="share-copy-link" />
                  {onInsert && (
                    <button data-testid="share-insert-into-chat"
                      onClick={() => { onInsert(bundleShareUrl(created.token)); onClose?.(); }}
                      style={{ background: "linear-gradient(135deg,#25D366,#128C7E)",
                                color: "white", border: "none", borderRadius: 6,
                                padding: "8px 14px", cursor: "pointer",
                                fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                                display: "inline-flex", alignItems: "center", gap: 6 }}>
                      Вставить в чат и закрыть
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conversion dashboard */}
        {stats && stats.totals && stats.totals.shares > 0 && (
          <FunnelDashboard stats={stats} />
        )}

        {/* My existing bundles */}
        {myShares.length > 0 && (
          <div style={{ padding: "16px 24px 24px", borderTop: "1px solid #f1f1ee",
                        background: "#faf9f7" }}>
            <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                          letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>
              Мои подборки
            </div>
            {myShares.slice(0, 10).map(s => {
              const expired = s.expires_at && new Date(s.expires_at) < new Date();
              return (
                <div key={s.id} data-testid={`share-my-${s.token}`}
                  style={{ display: "flex", alignItems: "center", gap: 12,
                           padding: "8px 0", borderBottom: "1px solid #ecebe6",
                           opacity: expired ? 0.55 : 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: "#374151",
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {bundleShareUrl(s.token)}
                    </div>
                    <div style={{ fontFamily: "Manrope", fontSize: 10, color: "#8a8a89", marginTop: 2 }}>
                      {s.product_ids.length} тов. · 👁 {s.view_count} · 🖱 {s.login_click_count || 0} · 📝 {s.signup_count}
                      {expired ? " · истекла" : ` · до ${new Date(s.expires_at).toLocaleString("ru-RU",
                            { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                    </div>
                  </div>
                  {!expired && <CopyLinkButton url={bundleShareUrl(s.token)} compact
                                  testid={`share-recopy-${s.token}`} />}
                  <button onClick={() => deleteBundle(s.id)} data-testid={`share-delete-${s.token}`}
                    title="Удалить" style={iconBtn}><Trash2 size={12} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// --------------- helpers ---------------

const backdrop = {
  position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
  zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
};
const modal = {
  background: "white", borderRadius: 12, maxWidth: 720, width: "100%",
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)", margin: "auto",
};
const header = {
  padding: "20px 24px 12px",
  display: "flex", alignItems: "flex-start", justifyContent: "space-between",
  borderBottom: "1px solid #f1f1ee", marginBottom: 12,
};
const xBtn = { background: "none", border: "none", cursor: "pointer", padding: 4, color: "#8a8a89" };
const inp = {
  padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6,
  fontFamily: "Manrope", fontSize: 12, outline: "none", background: "white",
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 14px",
  background: "linear-gradient(135deg, #C5A059, #D4B878)",
  color: "#0A0A0A", border: "none", borderRadius: 6,
  fontFamily: "Manrope", fontSize: 12, fontWeight: 700, cursor: "pointer",
};
const btnSecondary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 14px",
  background: "white",
  color: "#374151", border: "1px solid #d1d5db", borderRadius: 6,
  fontFamily: "Manrope", fontSize: 12, fontWeight: 600, cursor: "pointer",
};
const iconBtn = {
  background: "transparent", border: "1px solid #e5e7eb",
  borderRadius: 4, padding: 5, cursor: "pointer", color: "#64748b",
  display: "flex", alignItems: "center",
};

function humanize(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length && typeof d[0]?.msg === "string") return d[0].msg;
  return fallback;
}

// -------- Funnel dashboard --------
// Shows the manager (or admin/senior) their weekly conversion funnel for
// share bundles: views → login-clicks → registrations → purchases.
// Every step is a clickable step with count + % relative to views.
function FunnelDashboard({ stats }) {
  const t = stats.totals;
  const pct = (n) => t.views > 0 ? Math.round((n / t.views) * 100) : 0;
  const steps = [
    { icon: Eye, label: "Просмотры", value: t.views, tone: "#0369a1", pct: 100 },
    { icon: MousePointerClick, label: "Нажали «Войти»", value: t.login_clicks,
      tone: "#7c3aed", pct: pct(t.login_clicks) },
    { icon: UserPlus, label: "Зарегистрировались", value: t.signups,
      tone: "#059669", pct: pct(t.signups) },
    { icon: ShoppingBag, label: "Купили", value: t.purchases,
      tone: "#C5A059", pct: pct(t.purchases) },
  ];
  return (
    <div data-testid="share-funnel-dashboard"
      style={{ padding: "16px 24px 4px", borderTop: "1px solid #f1f1ee",
                background: "#fdfcf8" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <TrendingUp size={16} color="#C5A059" />
        <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89",
                       letterSpacing: 1.5, textTransform: "uppercase" }}>
          Конверсия за {stats.period_days} дн. · {t.shares} подборок
        </div>
      </div>
      <div style={{ display: "grid",
                     gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
                     gap: 10, marginBottom: 12 }}>
        {steps.map((s) => (
          <div key={s.label} data-testid={`share-funnel-step-${s.label}`}
            style={{ background: "white", border: "1px solid #efeee9",
                      borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <s.icon size={13} color={s.tone} />
              <span style={{ fontFamily: "Manrope", fontSize: 10, color: "#6b7280",
                              textTransform: "uppercase", letterSpacing: 0.5 }}>
                {s.label}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontFamily: "Cormorant Garamond", fontSize: 22,
                              fontWeight: 600, color: s.tone }}>
                {s.value}
              </span>
              <span style={{ fontFamily: "Manrope", fontSize: 11, color: "#9ca3af" }}>
                {s.pct}%
              </span>
            </div>
          </div>
        ))}
      </div>
      {stats.per_manager && stats.per_manager.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontFamily: "Manrope", fontSize: 10, color: "#8a8a89",
                         letterSpacing: 1.2, textTransform: "uppercase",
                         marginBottom: 6 }}>
            По менеджерам
          </div>
          <div style={{ background: "white", border: "1px solid #efeee9",
                         borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
                             fontFamily: "Manrope", fontSize: 12 }}>
              <thead style={{ background: "#faf9f5" }}>
                <tr>
                  <th style={dashTh}>Менеджер</th>
                  <th style={dashTh}>Ссылки</th>
                  <th style={dashTh}>Просм.</th>
                  <th style={dashTh}>Клики</th>
                  <th style={dashTh}>Регистр.</th>
                  <th style={dashTh}>Куп.</th>
                </tr>
              </thead>
              <tbody>
                {stats.per_manager.map((m) => (
                  <tr key={m.id} data-testid={`share-funnel-mgr-${m.id}`}
                    style={{ borderTop: "1px solid #f1f1ee" }}>
                    <td style={dashTd}>{m.name}</td>
                    <td style={dashTd}>{m.shares}</td>
                    <td style={dashTd}>{m.views}</td>
                    <td style={dashTd}>{m.login_clicks}</td>
                    <td style={dashTd}>{m.signups}</td>
                    <td style={{ ...dashTd, fontWeight: 700, color: "#C5A059" }}>
                      {m.purchases}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const dashTh = { textAlign: "left", padding: "8px 10px", color: "#374151",
                  fontWeight: 600, fontSize: 10, textTransform: "uppercase",
                  letterSpacing: 0.4 };
const dashTd = { padding: "8px 10px", color: "#111", verticalAlign: "middle" };

// -------- Manual product picker --------
// Search box + list of matching visible products; click "+" to add
// to the bundle. Uses the same filters row (category/brand/price) as
// the auto-suggest so both flows respect the manager's constraints.
function ManualPicker({ filters, onAdd, selectedIds, atMax }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 24 };
        if (q.trim()) params.q = q.trim();
        if (filters.category_slug) params.category_slug = filters.category_slug;
        if (filters.brand) params.brand = filters.brand;
        if (filters.price_min) params.price_min = Number(filters.price_min);
        if (filters.price_max) params.price_max = Number(filters.price_max);
        const r = await axios.get(`${API}/admin/shares/search`, {
          params, withCredentials: true,
        });
        setResults(r.data.items || []);
      } catch { /* noop */ }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, filters.category_slug, filters.brand, filters.price_min, filters.price_max]);

  return (
    <div data-testid="share-manual-panel"
      style={{ margin: "0 24px 12px", background: "#fafaf9",
                border: "1px solid #efeee9", borderRadius: 8, padding: 12 }}>
      <div style={{ position: "relative", marginBottom: 10 }}>
        <Search size={14} style={{ position: "absolute", top: "50%", left: 10,
                                     transform: "translateY(-50%)", color: "#9ca3af" }} />
        <input data-testid="share-manual-search" value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Поиск по названию или SKU"
          style={{ ...inp, width: "100%", paddingLeft: 32 }} />
      </div>
      {atMax && (
        <div style={{ padding: "6px 10px", background: "#fff7ed", border: "1px solid #fed7aa",
                       borderRadius: 6, fontFamily: "Manrope", fontSize: 11, color: "#9a3412",
                       marginBottom: 8 }}>
          В подборке уже 5 товаров — уберите один, чтобы добавить другой.
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {loading && results.length === 0 && (
          <div style={{ padding: "16px 8px", fontFamily: "Manrope", fontSize: 12, color: "#6b7280",
                         display: "flex", alignItems: "center", gap: 8 }}>
            <Loader2 size={14} className="animate-spin" /> Поиск…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div style={{ padding: "16px 8px", fontFamily: "Manrope", fontSize: 12, color: "#9ca3af" }}>
            Ничего не найдено. Попробуйте изменить фильтры выше.
          </div>
        )}
        {results.map((p) => {
          const already = selectedIds.includes(p.id);
          return (
            <div key={p.id} data-testid={`share-manual-item-${p.id}`}
              style={{ display: "flex", gap: 10, alignItems: "center",
                        padding: "6px 0", borderBottom: "1px solid #efeee9" }}>
              <div style={{ width: 44, height: 56, background: "#f5f5f4",
                             borderRadius: 4, overflow: "hidden", flexShrink: 0 }}>
                {p.image && <img src={resolveImageUrl(p.image)} alt={p.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#0A0A0A",
                               fontWeight: 600, lineHeight: 1.25,
                               overflow: "hidden", display: "-webkit-box",
                               WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {p.name}
                </div>
                <div style={{ fontFamily: "Manrope", fontSize: 10, color: "#8a8a89", marginTop: 2 }}>
                  {p.brand} · {Number(p.price).toLocaleString("ru-RU")} ₸
                  {p.sku ? ` · ${p.sku}` : ""}
                </div>
              </div>
              <button data-testid={`share-manual-add-${p.id}`}
                onClick={() => onAdd(p)} disabled={already || atMax}
                title={already ? "Уже в подборке" : "Добавить"}
                style={{ ...iconBtn,
                          background: already ? "#e5e7eb" : "#ecfdf5",
                          color: already ? "#9ca3af" : "#047857",
                          borderColor: already ? "#d1d5db" : "#a7f3d0",
                          cursor: already || atMax ? "not-allowed" : "pointer" }}>
                {already ? <Check size={13} /> : <Plus size={13} />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
